/**
 * /financials — owner-level P&L dashboard (Aug 21, 2026).
 *
 * Revenue · COGS (materials / labor / commissions) · gross + operating profit ·
 * cycle times · backlog · cash flow · 13-week forecast · monthly trend ·
 * breakdowns (carrier/zip/city/RM/TL/ASM/RS/source) · roof-specialist table ·
 * supplement capture · claims lens (carrier × zip) · material-invoice
 * ingestion · job-by-job P&L.
 *
 * All math lives in src/lib/financialsData.ts (single source of truth — the
 * SQL replay verification diffs against that module, not this file).
 * Visible to admin / sales_director / finance + supers only.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { useRealtimeTables } from "../lib/useRealtimeTable";
import { fmtUSD } from "../lib/analytics";
import { Link } from "react-router-dom";
import DealLink from "../components/DealLink";
import MaterialInvoicesPanel from "../components/financials/MaterialInvoicesPanel";
import TeamCommissionsSection from "../components/financials/TeamCommissionsSection";
import {
  fetchFinancialsRaw, fetchCycleStats, num,
  periodBounds, prevPeriodBounds, targetForPeriod, deltaOf,
  indexBy, commissionSplitByDeal, reviewSpiffByDeal, pnlForPeriod, soldCount, collectedInPeriod,
  backlogStats, cashFlowStats, monthlyTrend, breakdownRows, suppCapture, revenueBasis,
  claimsFilter, claimsAgg, carrierDisplay, forecast13w, rsPerformance, rsPreApprovalSummary,
  PERIOD_LABELS,
  type PeriodKey, type FinancialsRaw, type CycleStat, type BreakdownDim,
  type JobCost, type ClaimRow, type CommSplit,
  BREAKDOWN_LABELS,
} from "../lib/financialsData";

const PERIOD_KEYS: PeriodKey[] = ["this_month", "last_month", "this_quarter", "ytd", "t12", "custom"];

// ── tiny presentational helpers ───────────────────────────────────────────

function Chip({ tone, children, title }: { tone: "amber" | "slate" | "emerald" | "rose" | "sky"; children: React.ReactNode; title?: string }) {
  const map = {
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
  } as const;
  return (
    <span title={title} className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-bold border whitespace-nowrap ${map[tone]}`}>
      {children}
    </span>
  );
}

function DeltaChip({ cur, prev, goodUp = true }: { cur: number; prev: number; goodUp?: boolean }) {
  const d = deltaOf(cur, prev);
  if (d.pct === null) return <span className="text-[10.5px] text-slate-400">no prior data</span>;
  const good = goodUp ? d.up : !d.up;
  return (
    <span className={`text-[10.5px] font-bold ${good ? "text-emerald-600" : "text-rose-600"}`}>
      {d.up ? "▲" : "▼"} {Math.abs(d.pct).toFixed(0)}% vs prior
    </span>
  );
}

function pctCls(p: number | null): string {
  if (p === null) return "text-slate-400";
  if (p >= 35) return "text-emerald-600";
  if (p >= 25) return "text-amber-600";
  return "text-rose-600";
}

// ── page ──────────────────────────────────────────────────────────────────

// Greg's Aug 21 carve-out: this board is Greg / Chad / Shannon / Jennifer only.
// Jeremy is a super user (bypasses role gates), so the exclusion is by email —
// enforced here AND server-side (financials_gate + material_invoices RLS).
const EXCLUDED_EMAILS = new Set(["jcohan@bardinroofing.com"]);

export default function Financials() {
  const { member, role, isSuperUser } = useAuth();
  // finance_settings fs_write RLS is ADMIN-only (supers resolve to admin) —
  // showing the editor to finance/sales_director produced a silent 0-row
  // UPDATE for Shannon (audit finding). Gate the editor to who can actually save.
  const canEdit = isSuperUser || role === "admin";

  const [raw, setRaw] = useState<FinancialsRaw | null>(null);
  const [cycle, setCycle] = useState<CycleStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [period, setPeriod] = useState<PeriodKey>("ytd");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [dim, setDim] = useState<BreakdownDim>("carrier");
  const [rsCarrier, setRsCarrier] = useState("");
  const [clCarrier, setClCarrier] = useState("");
  const [clZip, setClZip] = useState("");
  const [clFrom, setClFrom] = useState("");
  const [clTo, setClTo] = useState("");
  const [jobSort, setJobSort] = useState<"margin" | "revenue" | "date">("margin");
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [editingSettings, setEditingSettings] = useState(false);
  const [ovhDraft, setOvhDraft] = useState("");
  const [tgtRevDraft, setTgtRevDraft] = useState("");
  const [tgtBuildsDraft, setTgtBuildsDraft] = useState("");
  const [tgtMarginDraft, setTgtMarginDraft] = useState("");

  const load = useCallback(async () => {
    try {
      setLoadErr(null);
      const r = await fetchFinancialsRaw();
      setRaw(r);
    } catch (e) {
      setLoadErr(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // realtime, debounced
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useRealtimeTables(["deals", "deal_payments", "roof_crew_payments", "material_invoices"], () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { void load(); }, 1200);
  });

  const bounds = useMemo(() => periodBounds(period, customFrom, customTo), [period, customFrom, customTo]);
  const prevB = useMemo(() => prevPeriodBounds(period, bounds), [period, bounds]);

  // Monotonic request id so a slow older period's response can't land last
  // (the documented stale-snapshot race class — UniversalSearch, Jul 14).
  const cycleReqRef = useRef(0);
  useEffect(() => {
    const reqId = ++cycleReqRef.current;
    fetchCycleStats(bounds.from, bounds.to)
      .then((rows) => { if (cycleReqRef.current === reqId) setCycle(rows); })
      .catch(() => { if (cycleReqRef.current === reqId) setCycle([]); });
  }, [bounds.from, bounds.to]);

  // ── memoized math ──
  const crewByDeal = useMemo(() => indexBy(raw?.crew ?? [], (r) => r.deal_id), [raw]);
  const invByDeal = useMemo(() => indexBy(raw?.invoices ?? [], (r) => r.deal_id), [raw]);
  const commSplit = useMemo(
    () => (raw
      ? commissionSplitByDeal(raw.deals, raw.members, raw.commissionPayments, {}, reviewSpiffByDeal(raw.spiffs))
      : new Map<string, CommSplit>()),
    [raw]);
  // Job cost: RM/TL/ASM + RS pool + the 5-Star Review SPIFF (commission on the job since Oct 19, 2026).
  const commByDeal = useMemo(() => {
    const m = new Map<string, number>();
    for (const [id, v] of commSplit) m.set(id, v.rmSide + v.rs + v.review);
    return m;
  }, [commSplit]);
  // Cash: timed by the commission flags. The review point is paid with the SPIFFs (cash.otherCompPeriod).
  const commCashByDeal = useMemo(() => {
    const m = new Map<string, number>();
    for (const [id, v] of commSplit) m.set(id, v.rmSide + v.rs);
    return m;
  }, [commSplit]);
  const pnl = useMemo(
    () => (raw ? pnlForPeriod(raw.deals, bounds, crewByDeal, invByDeal, commByDeal) : null),
    [raw, bounds, crewByDeal, invByDeal, commByDeal]);
  const pnlPrev = useMemo(
    () => (raw ? pnlForPeriod(raw.deals, prevB, crewByDeal, invByDeal, commByDeal) : null),
    [raw, prevB, crewByDeal, invByDeal, commByDeal]);
  const backlog = useMemo(() => (raw ? backlogStats(raw.deals) : null), [raw]);
  const cash = useMemo(() => (raw && pnl ? cashFlowStats(raw, bounds, commCashByDeal, commSplit) : null), [raw, pnl, bounds, commCashByDeal, commSplit]);
  const collectedPrev = useMemo(() => (raw ? collectedInPeriod(raw.payments, prevB) : 0), [raw, prevB]);
  const trend = useMemo(
    () => (raw ? monthlyTrend(raw.deals, crewByDeal, invByDeal, commByDeal, 12) : []),
    [raw, crewByDeal, invByDeal, commByDeal]);
  const bdRows = useMemo(
    () => (raw && pnl ? breakdownRows(dim, pnl, raw.members) : []),
    [raw, pnl, dim]);
  const supp = useMemo(() => (pnl ? suppCapture(pnl) : null), [pnl]);
  const rsPerf = useMemo(
    () => (raw && pnl ? rsPerformance(raw.rsRows, pnl, bounds, raw.members, rsCarrier) : []),
    [raw, pnl, bounds, rsCarrier]);
  const rsPreSummary = useMemo(() => rsPreApprovalSummary(rsPerf), [rsPerf]);
  // Carrier options for the RS filter — counts are deals in the RS universe
  // (has a meeting or a completed climb), so the list matches what filtering
  // can actually surface.
  const rsCarriers = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of raw?.rsRows ?? []) m.set(r.carrier ?? "", (m.get(r.carrier ?? "") ?? 0) + 1);
    return [...m.entries()].sort((a, z) => z[1] - a[1]);
  }, [raw]);
  const forecast = useMemo(
    () => (raw ? forecast13w(raw, commCashByDeal, cycle, crewByDeal, invByDeal) : null),
    [raw, commCashByDeal, cycle, crewByDeal, invByDeal]);

  const claimsBounds = useMemo(() => {
    const rows = raw?.claims ?? [];
    if (!rows.length) return { min: "2025-01-01", max: new Date().toISOString().slice(0, 10) };
    let min = rows[0].filed, max = rows[0].filed;
    for (const r of rows) { if (r.filed < min) min = r.filed; if (r.filed > max) max = r.filed; }
    return { min, max };
  }, [raw]);
  const clRows = useMemo(
    () => claimsFilter(raw?.claims ?? [], clCarrier === "__none" ? "" : clCarrier, clZip, clFrom || claimsBounds.min, clTo || claimsBounds.max,
      clCarrier === "__none"),
    [raw, clCarrier, clZip, clFrom, clTo, claimsBounds]);
  const clA = useMemo(() => claimsAgg(clRows), [clRows]);
  const clCarriers = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of raw?.claims ?? []) m.set(r.carrier, (m.get(r.carrier) ?? 0) + 1);
    return [...m.entries()].sort((a, z) => z[1] - a[1]);
  }, [raw]);
  const clZips = useMemo(() => {
    const m = new Map<string, { n: number; city: string }>();
    for (const r of raw?.claims ?? []) {
      if (!r.zip) continue;
      const cur = m.get(r.zip) ?? { n: 0, city: r.city };
      cur.n += 1; m.set(r.zip, cur);
    }
    return [...m.entries()].sort((a, z) => z[1].n - a[1].n);
  }, [raw]);
  const clDim: 0 | 1 = clCarrier && !clZip ? 1 : 0;
  const clGroups = useMemo(() => {
    const g = new Map<string, ClaimRow[]>();
    for (const r of clRows) {
      const k = clDim === 0 ? r.carrier : (r.zip || "(no zip)");
      const arr = g.get(k); if (arr) arr.push(r); else g.set(k, [r]);
    }
    return [...g.entries()].sort((a, z) => z[1].length - a[1].length).slice(0, 14);
  }, [clRows, clDim]);

  const sortedJobs = useMemo(() => {
    if (!pnl) return [] as JobCost[];
    const js = [...pnl.jobs];
    if (jobSort === "margin") {
      js.sort((a, z) => (a.marginPct ?? 999) - (z.marginPct ?? 999));
    } else if (jobSort === "revenue") {
      js.sort((a, z) => z.revenue - a.revenue);
    } else {
      js.sort((a, z) => (z.deal.build_completed_date ?? "").localeCompare(a.deal.build_completed_date ?? ""));
    }
    return js;
  }, [pnl, jobSort]);

  // overhead + operating profit
  const overheadPeriod = raw ? targetForPeriod(raw.monthlyOverhead, bounds) : null;
  const operatingProfit = pnl && overheadPeriod !== null ? pnl.gross - overheadPeriod : null;
  const breakEvenMonthly = raw && pnl && pnl.marginPct && raw.monthlyOverhead > 0
    ? raw.monthlyOverhead / (pnl.marginPct / 100) : null;

  // materials $/sq jump detection (last two months with data)
  const basis = useMemo(
    () => (raw ? revenueBasis(raw.deals, raw.externalRevenue, raw.revenueSeries) : []),
    [raw],
  );
  const matTrendMonths = trend.filter((m) => m.matPerSq !== null);
  // Calendar-YTD revenue vs the same span last year. Only sums months that have
  // a prior-year counterpart, so the headline can't compare against a blank.
  const yoyYtd = useMemo(() => {
    const yr = new Date().getFullYear();
    const rows = trend.filter((m) => Number(m.ym.slice(0, 4)) === yr && m.prevRevenue !== null);
    const cur = rows.reduce((s, m) => s + m.revenue, 0);
    const prv = rows.reduce((s, m) => s + (m.prevRevenue ?? 0), 0);
    return prv > 0 ? ((cur - prv) / prv) * 100 : null;
  }, [trend]);
  const matJump = matTrendMonths.length >= 2
    ? (() => {
        const a = matTrendMonths[matTrendMonths.length - 2].matPerSq ?? 0;
        const b = matTrendMonths[matTrendMonths.length - 1].matPerSq ?? 0;
        return a > 0 ? ((b - a) / a) * 100 : 0;
      })()
    : 0;

  const revTarget = raw ? targetForPeriod(raw.targets.revenue, bounds) : null;
  const buildsTarget = raw ? targetForPeriod(raw.targets.builds, bounds) : null;

  function openSettings() {
    if (!raw) return;
    setEditingSettings(true);
    setOvhDraft(String(raw.monthlyOverhead || ""));
    setTgtRevDraft(String(raw.targets.revenue || ""));
    setTgtBuildsDraft(String(raw.targets.builds || ""));
    setTgtMarginDraft(String(raw.targets.margin_pct || ""));
  }

  async function saveSettings() {
    if (!raw) return;
    const updates = [
      supabase.from("finance_settings").update({ value_jsonb: num(ovhDraft), updated_by: member?.id ?? null }).eq("key", "monthly_overhead").select("key"),
      supabase.from("finance_settings").update({
        value_jsonb: { revenue: num(tgtRevDraft), builds: num(tgtBuildsDraft), margin_pct: num(tgtMarginDraft) },
        updated_by: member?.id ?? null,
      }).eq("key", "financial_targets").select("key"),
    ];
    const res = await Promise.all(updates);
    const bad = res.find((r) => r.error);
    // RLS-blocked UPDATEs "succeed" with 0 rows — treat that as a failure too.
    const blocked = res.find((r) => !r.error && (r.data ?? []).length === 0);
    if (bad?.error) setLoadErr(`Settings save failed: ${bad.error.message}`);
    else if (blocked) setLoadErr("Settings save was blocked — only admin-tier users can change overhead/targets.");
    setEditingSettings(false);
    void load();
  }

  if (member?.email && EXCLUDED_EMAILS.has(member.email.toLowerCase())) {
    return (
      <div className="max-w-[1240px] mx-auto p-6">
        <div className="bg-slate-100 border border-slate-200 text-slate-600 rounded-xl p-4 text-sm">
          This board is limited to Greg, Chad, Shannon, and Jennifer.
        </div>
      </div>
    );
  }
  if (loading) {
    return <div className="max-w-[1240px] mx-auto p-6 text-slate-500">Loading financials…</div>;
  }
  if (loadErr || !raw || !pnl || !backlog || !cash) {
    return (
      <div className="max-w-[1240px] mx-auto p-6">
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm">
          Couldn't load the financials{loadErr ? `: ${loadErr}` : ""}. <button className="font-bold underline" onClick={() => { setLoading(true); void load(); }}>Retry</button>
        </div>
      </div>
    );
  }

  const cycleOf = (metric: string) => cycle.find((c) => c.metric === metric);

  return (
    <div className="max-w-[1240px] mx-auto px-4 py-5 pb-16 text-slate-900">
      {/* header + period */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-3">
        <div>
          <h1 className="text-[22px] font-extrabold tracking-tight">💹 Financials</h1>
          <div className="text-[13px] text-slate-500">Revenue · job costs · margin · cash flow — Bardin Roofing</div>
        </div>
        <div>
          <div className="flex gap-1.5 flex-wrap justify-end">
            {PERIOD_KEYS.map((k) => (
              <button key={k} onClick={() => setPeriod(k)}
                className={`px-3 py-1.5 rounded-full text-[12px] font-bold border transition ${period === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-orange-400 hover:text-orange-600"}`}>
                {PERIOD_LABELS[k]}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex gap-2 justify-end mt-1.5">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1 text-[12px]" />
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1 text-[12px]" />
            </div>
          )}
          <div className="text-[10.5px] text-slate-400 text-right mt-1">
            {bounds.from} → {bounds.to} · revenue recognized at build completion · cash items by payment date
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-3">
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-500">Revenue (billed)</div>
          <div className="text-[23px] font-extrabold tabular-nums text-orange-600">{fmtUSD(pnl.revenue)}</div>
          <div className="text-[10.5px] text-slate-500 flex gap-1.5 flex-wrap mt-0.5 mb-0.5">
            <span className="font-bold text-slate-700 tabular-nums">{fmtUSD(pnl.buildRevenue)}</span>
            <span>build</span>
            <span className="text-slate-300">·</span>
            <span className="font-bold text-sky-700 tabular-nums">{fmtUSD(pnl.supplementRevenue)}</span>
            <span>supplements{pnl.supplementPctOfRevenue !== null ? ` (${pnl.supplementPctOfRevenue.toFixed(0)}%)` : ""}</span>
          </div>
          <div className="text-[11px] text-slate-500 flex gap-2 flex-wrap">
            <DeltaChip cur={pnl.revenue} prev={pnlPrev?.revenue ?? 0} />
            {revTarget ? <span className={pnl.revenue >= revTarget ? "text-emerald-600 font-bold" : "text-amber-600 font-bold"}>{Math.round((pnl.revenue / revTarget) * 100)}% of target</span> : null}
          </div>
          {pnl.invoicePendingCount > 0 && (
            <div className="text-[10.5px] text-amber-600 mt-0.5">{pnl.invoicePendingCount} build{pnl.invoicePendingCount > 1 ? "s" : ""} not invoiced yet — will climb</div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-500">Collected</div>
          <div className="text-[23px] font-extrabold tabular-nums text-emerald-600">{fmtUSD(cash.collected)}</div>
          <div className="text-[11px] text-slate-500"><DeltaChip cur={cash.collected} prev={collectedPrev} /> · +{fmtUSD(cash.inFlight)} in flight</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-500">Gross margin</div>
          <div className={`text-[23px] font-extrabold tabular-nums ${pctCls(pnl.marginPct)}`}>
            {pnl.marginPct === null ? "—" : `${pnl.marginPct.toFixed(1)}%`}
          </div>
          {pnl.marginPct !== null && pnl.marginPctBeforeSupps !== null && (
            <div className="text-[10.5px] text-slate-500 flex gap-1.5 flex-wrap mt-0.5 mb-0.5">
              <span className={`font-bold tabular-nums ${pctCls(pnl.marginPctBeforeSupps)}`}>{pnl.marginPctBeforeSupps.toFixed(1)}%</span>
              <span>before supps</span>
              <span className="text-slate-300">·</span>
              <span className="font-bold text-sky-700 tabular-nums">+{(pnl.marginPct - pnl.marginPctBeforeSupps).toFixed(1)} pts</span>
              <span>from supps</span>
            </div>
          )}
          <div className="text-[11px] text-slate-500 flex gap-2 flex-wrap">
            <span>{fmtUSD(pnl.gross)} gross profit</span>
            {raw.targets.margin_pct > 0 && pnl.marginPct !== null && (
              <span className={pnl.marginPct >= raw.targets.margin_pct ? "text-emerald-600 font-bold" : "text-amber-600 font-bold"}>target {raw.targets.margin_pct}%</span>
            )}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-500">Coming up</div>
          <div className="text-[23px] font-extrabold tabular-nums text-sky-600">{fmtUSD(backlog.total)}</div>
          <div className="text-[11px] text-slate-500">{backlog.count} signed jobs not yet built</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-500">Builds completed</div>
          <div className="text-[23px] font-extrabold tabular-nums">{pnl.jobCount}</div>
          <div className="text-[11px] text-slate-500 flex gap-2 flex-wrap">
            <DeltaChip cur={pnl.jobCount} prev={pnlPrev?.jobCount ?? 0} />
            {buildsTarget ? <span className="font-bold text-slate-500">target {Math.round(buildsTarget)}</span> : null}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-500">Jobs sold</div>
          <div className="text-[23px] font-extrabold tabular-nums">{soldCount(raw.deals, bounds)}</div>
          <div className="text-[11px] text-slate-500"><DeltaChip cur={soldCount(raw.deals, bounds)} prev={soldCount(raw.deals, prevB)} /> · agreements signed (tracked since Jul '26)</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-500">Avg revenue / job</div>
          <div className="text-[23px] font-extrabold tabular-nums">{pnl.avgRevenue === null ? "—" : fmtUSD(pnl.avgRevenue)}</div>
          <div className="text-[11px] text-slate-500">{pnl.avgSquares !== null ? `${pnl.avgSquares.toFixed(1)} sq avg · ` : ""}{pnl.revPerSquare !== null ? `${fmtUSD(pnl.revPerSquare)}/square` : "squares pending"}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-500">Cycle times (median)</div>
          <div className="text-[12.5px] font-bold mt-1 space-y-0.5 tabular-nums">
            <div>claim → approval <span className="float-right">{cycleOf("claim_to_approval")?.median_days ?? "—"}d</span></div>
            <div>signed → build <span className="float-right">{cycleOf("signed_to_build")?.median_days ?? "—"}d</span></div>
            <div>build → paid in full <span className="float-right">{cycleOf("build_to_paid")?.median_days ?? "—"}d</span></div>
          </div>
        </div>
      </div>

      {/* P&L + backlog */}
      <div className="grid lg:grid-cols-12 gap-3 mb-3">
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-[15px] font-extrabold">📊 P&amp;L</h2>
            <Chip tone="slate">build-completion basis</Chip>
          </div>
          {[
            { label: "Build revenue", amt: pnl.buildRevenue, neg: false, chip: `${pnl.jobCount} jobs · commissionable basis` },
            { label: "Supplement revenue", amt: pnl.supplementRevenue, neg: false, chip: pnl.supplementPctOfRevenue !== null ? `${pnl.supplementPctOfRevenue.toFixed(1)}% of revenue · reps not paid on this` : "approved supplements" },
            { label: "Revenue", amt: pnl.revenue, neg: false, chip: `${pnl.jobCount} jobs` },
            { label: "Materials", amt: pnl.materials, neg: true, chip: `on ${pnl.materialsCoverage} of ${pnl.jobCount} jobs` },
            { label: "Labor (crew)", amt: pnl.labor, neg: true, chip: `on ${pnl.laborCoverage} of ${pnl.jobCount} jobs` },
            { label: "Commissions", amt: pnl.commissions, neg: true, chip: "RM + TL + ASM + RS pool + 5★ review, accrued" },
          ].map((r) => (
            <div key={r.label} className={`flex items-center justify-between py-2 gap-2 flex-wrap ${r.label === "Revenue" ? "border-t-2 border-slate-300 border-b border-dashed border-b-slate-200" : "border-b border-dashed border-slate-200"}`}>
              <div className="flex items-center gap-2">
                <span className={`text-[13.5px] ${r.label === "Revenue" ? "font-extrabold" : r.label === "Supplement revenue" ? "font-semibold text-sky-700" : "font-semibold"}`}>{r.label}</span>
                <Chip tone={r.label === "Supplement revenue" ? "sky" : r.label !== "Revenue" && (r.label === "Materials" ? pnl.materialsCoverage : r.label === "Labor (crew)" ? pnl.laborCoverage : pnl.jobCount) < pnl.jobCount ? "amber" : "slate"}>{r.chip}</Chip>
              </div>
              <span className={`font-bold tabular-nums ${r.neg ? "text-rose-600" : r.label === "Supplement revenue" ? "text-sky-700" : ""}`}>{r.neg ? "− " : ""}{fmtUSD(r.amt)}</span>
            </div>
          ))}
          {pnl.supplementPendingJobs > 0 && (
            <div className="mt-2 text-[11px] text-slate-500">
              ⏳ {pnl.supplementPendingJobs} of {pnl.jobCount} jobs have no supplement filed yet — approval lags the build by weeks, so supplement revenue on recent periods understates.
            </div>
          )}
          <div className="mt-2.5 bg-emerald-50 border border-emerald-200 rounded-lg px-3.5 py-2.5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10.5px] font-extrabold uppercase tracking-wide text-emerald-700">Gross profit</div>
                <div className="text-[10.5px] text-slate-500">materials + labor + commissions only</div>
              </div>
              <div className="text-right">
                <div className="text-[19px] font-extrabold tabular-nums text-emerald-700">{fmtUSD(pnl.gross)}</div>
                <div className={`text-[12px] font-bold ${pctCls(pnl.marginPct)}`}>{pnl.marginPct === null ? "—" : `${pnl.marginPct.toFixed(1)}% margin`}</div>
              </div>
            </div>
            {pnl.marginPct !== null && pnl.marginPctBeforeSupps !== null && (
              <div className="mt-1.5 pt-1.5 border-t border-emerald-200/70 text-[11px] flex items-center gap-1.5 flex-wrap tabular-nums">
                <span className="text-slate-600">before supps</span>
                <span className={`font-bold ${pctCls(pnl.marginPctBeforeSupps)}`}>{pnl.marginPctBeforeSupps.toFixed(1)}%</span>
                <span className="text-slate-500">({fmtUSD(pnl.grossBeforeSupps)})</span>
                <span className="text-slate-400">→</span>
                <span className="text-slate-600">after supps</span>
                <span className={`font-bold ${pctCls(pnl.marginPct)}`}>{pnl.marginPct.toFixed(1)}%</span>
                <span className="font-bold text-sky-700">+{(pnl.marginPct - pnl.marginPctBeforeSupps).toFixed(1)} pts from supplements</span>
              </div>
            )}
          </div>
          {/* overhead → operating */}
          <div className="mt-2 text-[12.5px] flex items-center justify-between flex-wrap gap-2">
            {raw.monthlyOverhead > 0 ? (
              <>
                <span className="text-slate-600">
                  − Overhead {fmtUSD(overheadPeriod ?? 0)} <span className="text-slate-400">({fmtUSD(raw.monthlyOverhead)}/mo)</span>
                  {" "}→ <b>Operating profit {fmtUSD(operatingProfit ?? 0)}</b>
                </span>
                {breakEvenMonthly !== null && (
                  <Chip tone="sky" title="Overhead ÷ gross margin %">break-even ≈ {fmtUSD(breakEvenMonthly)}/mo of builds</Chip>
                )}
              </>
            ) : (
              <span className="text-slate-500">Set your monthly overhead to see operating profit + break-even {canEdit && (
                <button className="text-sky-700 font-bold underline" onClick={openSettings}>set it →</button>
              )}</span>
            )}
            {raw.monthlyOverhead > 0 && canEdit && (
              <button className="text-[11px] text-slate-400 underline" onClick={openSettings}>edit overhead / targets</button>
            )}
          </div>
          {editingSettings && (
            <div className="mt-2 border border-sky-200 bg-sky-50/50 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[12px]">
              {([
                ["Monthly overhead $", ovhDraft, setOvhDraft],
                ["Revenue target $/mo", tgtRevDraft, setTgtRevDraft],
                ["Builds target /mo", tgtBuildsDraft, setTgtBuildsDraft],
                ["Margin target %", tgtMarginDraft, setTgtMarginDraft],
              ] as Array<[string, string, (v: string) => void]>).map(([label, val, set]) => (
                <label key={label} className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
                  <input value={val} onChange={(e) => set(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5 bg-white" />
                </label>
              ))}
              <div className="col-span-2 md:col-span-4 flex gap-2">
                <button onClick={() => void saveSettings()} className="px-3 py-1.5 rounded-lg bg-sky-600 text-white text-[12px] font-bold">Save</button>
                <button onClick={() => setEditingSettings(false)} className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-[12px] font-bold">Cancel</button>
              </div>
            </div>
          )}
          <div className="mt-2 pt-2 border-t border-slate-200 text-[11.5px] text-slate-500 flex gap-3 flex-wrap">
            <span>Other comp (paid this period, not per-job COGS):</span>
            <span><b>{fmtUSD(cash.otherCompPeriod)}</b> SPIFFs + canvasser pay</span>
          </div>
        </div>

        {/* backlog */}
        <div className="lg:col-span-5 bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-[15px] font-extrabold">📅 Coming up</h2>
            <Chip tone="sky">signed · not yet built</Chip>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-[24px] font-extrabold text-sky-600 tabular-nums">{backlog.count}</span>
            <span className="text-[16px] font-extrabold tabular-nums">{fmtUSD(backlog.total)} contracted</span>
          </div>
          <div className="space-y-1.5 text-[12.5px]">
            <div className="flex justify-between bg-orange-50 rounded-lg px-2.5 py-1.5">
              <span>🔨 Building in the next 14 days</span><b className="tabular-nums">{backlog.next14} jobs</b>
            </div>
            <div className="flex justify-between bg-emerald-50 rounded-lg px-2.5 py-1.5">
              <span>🗓 On the build calendar</span><b className="tabular-nums">{backlog.scheduled} · {fmtUSD(backlog.scheduledAmt)}</b>
            </div>
            <div className="flex justify-between bg-slate-100 rounded-lg px-2.5 py-1.5">
              <span>⏳ Awaiting schedule / approvals</span><b className="tabular-nums">{backlog.unscheduled} · {fmtUSD(backlog.unscheduledAmt)}</b>
            </div>
          </div>
          <div className="mt-2 flex gap-2 flex-wrap">
            {backlog.medianAgeDays !== null && (
              <Chip tone={backlog.medianAgeDays > 30 ? "amber" : "slate"} title="Days since signing (or since entering the current stage on legacy deals)">median days waiting: {backlog.medianAgeDays}d</Chip>
            )}
            {backlog.stale30 > 0 && <Chip tone="amber">⚠ {backlog.stale30} unscheduled &gt;30d</Chip>}
            {backlog.stale60 > 0 && <Chip tone="rose">⚠ {backlog.stale60} &gt;60d — cancellation risk</Chip>}
          </div>
          <div className="text-[10.5px] text-slate-400 mt-2">Contracted $ = live scope math (same source as the signing page). Supplements landing after build add on top.</div>
        </div>
      </div>

      {/* Cash flow */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-3">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[15px] font-extrabold">💵 Cash flow — paid &amp; unpaid</h2>
          <Chip tone="slate">materials · labor · commissions</Chip>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="bg-emerald-50 text-emerald-700 text-[11px] font-extrabold uppercase tracking-wide px-3 py-2">Money in</div>
            {[
              [`Collected this period`, cash.collected],
              [`In flight (iink + ACH settling)`, cash.inFlight],
              [`AR outstanding (${cash.arAccounts} accounts)`, cash.arOutstanding],
            ].map(([l, v]) => (
              <div key={String(l)} className="flex justify-between px-3 py-1.5 border-t border-dashed border-slate-200 text-[12.5px]"><span>{l}</span><b className="tabular-nums">{fmtUSD(Number(v))}</b></div>
            ))}
            <div className="flex justify-between px-3 py-2 border-t-2 border-slate-200 bg-slate-50 text-[13px] font-extrabold"><span>Still coming to you</span><span className="text-emerald-700 tabular-nums">{fmtUSD(cash.expectedIn)}</span></div>
          </div>
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="bg-slate-100 text-slate-600 text-[11px] font-extrabold uppercase tracking-wide px-3 py-2">Money out — paid this period</div>
            {[
              ["Material invoices paid", cash.matPaidPeriod],
              ["Crew labor paid", cash.crewPaidPeriod],
              ["Commissions paid", cash.commPaidPeriod],
              ["SPIFFs + canvasser pay", cash.otherCompPeriod],
            ].map(([l, v]) => (
              <div key={String(l)} className="flex justify-between px-3 py-1.5 border-t border-dashed border-slate-200 text-[12.5px]"><span>{l}</span><b className="tabular-nums">{fmtUSD(Number(v))}</b></div>
            ))}
            <div className="flex justify-between px-3 py-2 border-t-2 border-slate-200 bg-slate-50 text-[13px] font-extrabold"><span>Paid out</span><span className="tabular-nums">{fmtUSD(cash.matPaidPeriod + cash.crewPaidPeriod + cash.commPaidPeriod + cash.otherCompPeriod)}</span></div>
          </div>
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="bg-rose-50 text-rose-700 text-[11px] font-extrabold uppercase tracking-wide px-3 py-2">Money out — owed right now</div>
            <div className="flex justify-between px-3 py-1.5 border-t border-dashed border-slate-200 text-[12.5px]">
              <span>SRS invoices unpaid ({cash.matUnpaidCount}){cash.matOverdue > 0 && <span className="text-rose-600 font-bold"> · {fmtUSD(cash.matOverdue)} overdue</span>}</span>
              <b className="tabular-nums">{fmtUSD(cash.matUnpaid)}</b>
            </div>
            <div className="flex justify-between px-3 py-1.5 border-t border-dashed border-slate-200 text-[12.5px]"><span>Crew approved, unpaid ({cash.crewOwedCount})</span><b className="tabular-nums">{fmtUSD(cash.crewOwed)}</b></div>
            <div className="flex justify-between px-3 py-1.5 border-t border-dashed border-slate-200 text-[12.5px]"><span>Commissions approved, unpaid ({cash.commOwedApprovedCount})</span><b className="tabular-nums">{fmtUSD(cash.commOwedApproved)}</b></div>
            <div className="flex justify-between px-3 py-1.5 border-t border-dashed border-slate-200 text-[12.5px]"><span>Commission queue pending ({cash.commPendingCount})</span><b className="tabular-nums">{fmtUSD(cash.commPendingQueue)}</b></div>
            <div className="flex justify-between px-3 py-2 border-t-2 border-slate-200 bg-slate-50 text-[13px] font-extrabold"><span>Owed</span><span className="text-rose-600 tabular-nums">{fmtUSD(cash.owedOut)}</span></div>
          </div>
        </div>
        <div className="mt-3 bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 rounded-xl px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
          <span className="text-[12.5px]"><b>Net position:</b> expected in ({fmtUSD(cash.expectedIn)}) minus owed ({fmtUSD(cash.owedOut)})</span>
          <span className={`text-[18px] font-extrabold tabular-nums ${cash.net >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{cash.net >= 0 ? "+" : ""}{fmtUSD(cash.net)}</span>
        </div>
      </div>

      {/* Team commissions — per-person monthly + YTD (rides this page's raw pull) */}
      <TeamCommissionsSection deals={raw.deals} members={raw.members} payments={raw.commissionPayments} />

      {/* 13-week forecast */}
      {forecast && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[15px] font-extrabold">🔮 13-week cash forecast</h2>
            <Chip tone="amber">projection — sharpens as data accumulates</Chip>
          </div>
          <div className="overflow-x-auto">
            <div className="flex gap-1.5 min-w-[820px]">
              {forecast.weeks.map((w) => {
                const maxAbs = Math.max(1, ...forecast.weeks.map((x) => Math.max(x.inflow, x.outflow)));
                return (
                  <div key={w.start} className="flex-1 text-center" title={`Week of ${w.label}: in ${fmtUSD(w.inflow)} · out ${fmtUSD(w.outflow)} · running ${fmtUSD(w.cum)}`}>
                    <div className="h-[90px] flex items-end justify-center gap-0.5">
                      <div className="w-2.5 rounded-t bg-emerald-400" style={{ height: `${(w.inflow / maxAbs) * 100}%` }} />
                      <div className="w-2.5 rounded-t bg-rose-300" style={{ height: `${(w.outflow / maxAbs) * 100}%` }} />
                    </div>
                    <div className="text-[9.5px] font-bold text-slate-500 mt-0.5">{w.label}</div>
                    <div className={`text-[9.5px] font-bold tabular-nums ${w.cum >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtUSD(w.cum, { short: true })}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="text-[10.5px] text-slate-500 mt-2">
            <span className="inline-block w-2 h-2 rounded-sm bg-emerald-400 mr-1" />in ·
            <span className="inline-block w-2 h-2 rounded-sm bg-rose-300 mx-1" />out · number = running net.
            {" "}Assumptions: {forecast.assumptions.join(" · ")}.
          </div>
        </div>
      )}

      {/* Monthly trend */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-3">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h2 className="text-[15px] font-extrabold">📈 Monthly trend — trailing 12 vs last year</h2>
          <div className="flex gap-2">
            {Math.abs(matJump) >= 5 && matTrendMonths.length >= 2 && (
              <Chip tone={matJump > 0 ? "rose" : "emerald"}>
                materials {matJump > 0 ? "▲" : "▼"} {Math.abs(matJump).toFixed(0)}%/sq vs prior month
              </Chip>
            )}
            {yoyYtd !== null && (
              <Chip tone={yoyYtd >= 0 ? "emerald" : "rose"}>
                YTD {yoyYtd >= 0 ? "▲" : "▼"} {Math.abs(yoyYtd).toFixed(0)}% vs last year
              </Chip>
            )}
            <Chip tone="slate">solid = this year · ghost = same month last year</Chip>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="flex items-end gap-2 h-[150px] min-w-[760px]">
            {trend.map((m) => {
              const max = Math.max(1, ...trend.flatMap((x) => [x.revenue, x.prevRevenue ?? 0]));
              const tip = [
                `${m.label}: ${fmtUSD(m.revenue)} · ${m.jobs} jobs · COGS ${fmtUSD(m.cogs)}`,
                m.matPerSq ? `$${Math.round(m.matPerSq)}/sq materials` : "",
                m.prevRevenue === null
                  ? "no prior-year history"
                  : `last year: ${fmtUSD(m.prevRevenue)} · ${m.prevJobs} jobs${m.prevClippedTo ? ` (clipped to ${m.prevClippedTo} to match month-to-date)` : ""}`,
              ].filter(Boolean).join(" · ");
              return (
                <div key={m.ym} className="flex-1 flex flex-col items-center justify-end h-full" title={tip}>
                  <div className="text-[9.5px] font-bold text-slate-500">{fmtUSD(m.revenue, { short: true })}</div>
                  <div className="relative w-full max-w-[52px] flex-1 flex items-end">
                    {m.prevRevenue !== null && (
                      <div className="absolute inset-x-0 bottom-0 rounded-t-md bg-slate-200 border-t border-slate-300"
                        style={{ height: `${(m.prevRevenue / max) * 100}%` }} />
                    )}
                    <div className={`relative w-full rounded-t-md ${m.partial ? "bg-orange-300" : "bg-gradient-to-b from-orange-400 to-orange-600"}`}
                      style={{ height: `${(m.revenue / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 min-w-[760px] mt-1">
            {trend.map((m) => (
              <div key={m.ym} className="flex-1 text-center">
                <div className="text-[10.5px] font-bold text-slate-600">{m.label}</div>
                <div className="text-[9.5px] text-slate-400">{m.jobs} jobs</div>
                <div className={`text-[10px] font-bold ${pctCls(m.marginPct)}`}>{m.marginPct === null ? "—" : `${m.marginPct.toFixed(0)}%`}</div>
                <div className={`text-[9.5px] font-bold ${m.yoyPct === null ? "text-slate-300" : m.yoyPct >= 0 ? "text-emerald-600" : "text-rose-600"}`}
                  title={m.yoyPct === null ? "no prior-year build history for this month" : "revenue vs same month last year"}>
                  {m.yoyPct === null ? "·" : `${m.yoyPct >= 0 ? "▲" : "▼"}${Math.abs(m.yoyPct).toFixed(0)}%`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue basis — both answers, side by side */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-3">
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <h2 className="text-[15px] font-extrabold">⚖️ Revenue by fiscal year</h2>
          <Chip tone="slate">both are right · they answer different questions</Chip>
        </div>
        <div className="text-[11.5px] text-slate-500 mb-3">
          <b>Revenue</b> is the reported number: QuickBooks through 2025, and for 2026 QuickBooks until it stopped
          carrying the detail, then this CRM. It splits into <b>build revenue</b> — the basis reps are paid on —
          and <b>supplements</b>, which they are not.
          <div className="mt-1.5">
            <b>Production</b> is a cross-check on a different basis: what this CRM recognized when roofs were
            completed, rather than when they were invoiced. It won&rsquo;t match month to month and isn&rsquo;t
            meant to — it&rsquo;s there to catch a year where the two stories genuinely disagree.
          </div>
        </div>

        {basis.length === 0 ? (
          <div className="text-[12px] text-slate-500">
            No QuickBooks months imported yet — the booked column fills in once a P&amp;L by Month export is loaded.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px] min-w-[820px]">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2">Fiscal year</th>
                  <th className="text-right">Revenue</th>
                  <th className="text-right">Build revenue</th>
                  <th className="text-right text-sky-700">Supplements</th>
                  <th className="text-right border-l border-slate-200">Production (CRM)</th>
                  <th className="text-right">Builds</th>
                </tr>
              </thead>
              <tbody>
                {basis.map((b) => (
                  <tr key={b.year} className="border-b border-dashed border-slate-100">
                    <td className="py-2 font-bold">
                      {b.year}
                      {b.inProgress && <span className="ml-1.5 text-[10.5px] font-normal text-slate-400">YTD</span>}
                    </td>
                    <td className="text-right tabular-nums font-extrabold">
                      {b.officialRevenue === null ? <span className="text-slate-300">—</span> : fmtUSD(b.officialRevenue)}
                      {b.officialRevenueSources.length > 0 && (
                        <div className="text-[9.5px] font-normal text-slate-400">
                          {b.officialRevenueSources.map((x) => x === "quickbooks" ? "QuickBooks" : "CRM").join(" + ")}
                        </div>
                      )}
                    </td>
                    <td className="text-right tabular-nums font-semibold">
                      {b.officialBuildRevenue === null ? <span className="text-slate-300">—</span> : fmtUSD(b.officialBuildRevenue)}
                    </td>
                    <td className="text-right tabular-nums font-semibold text-sky-700">
                      {b.officialSupplements === null ? <span className="text-slate-300">—</span> : (
                        <>
                          {fmtUSD(b.officialSupplements)}
                          {b.officialRevenue ? (
                            <span className="text-sky-400 font-normal"> ({(b.officialSupplements / b.officialRevenue * 100).toFixed(0)}%)</span>
                          ) : null}
                          {b.officialSupplementSources.length > 1 && (
                            <div className="text-[9.5px] font-normal text-slate-400">QuickBooks + CRM</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="text-right tabular-nums text-slate-500 border-l border-slate-200"
                      title="Roofs completed, per this CRM — a cross-check on a different basis, not the reported number.">
                      {b.productionIsLegacy ? (
                        <span>{fmtUSD(b.legacyProduction ?? 0)}<Chip tone="amber">Zoho</Chip></span>
                      ) : b.production === null ? <span className="text-slate-300">—</span> : fmtUSD(b.production)}
                    </td>
                    <td className="text-right tabular-nums text-slate-500">{b.productionBuilds || b.builds || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {basis.some((b) => (b.badDebt ?? 0) > 0 || b.nonCollect > 0) && (
          <div className="mt-3 border-t border-slate-200 pt-2.5">
            <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
              <h3 className="text-[13px] font-extrabold">📋 Non-collect revenue &amp; late fees</h3>
              <Chip tone="slate">billed, approved not to pursue</Chip>
            </div>
            <div className="text-[11.5px] text-slate-500 mb-2">
              QuickBooks invoices the <b>gross</b> amount and credits the non-collect portion back as a Bad Debt
              memo. This board nets it out of the invoice <b>up front</b>, so it never enters AR at all. Same
              outcome by two routes — which is why the two columns track each other.
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px] min-w-[600px]">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-1.5">Year</th>
                    <th className="text-right">QuickBooks credit memos</th>
                    <th className="text-right">CRM non-collect</th>
                    <th className="text-right">Difference</th>
                    <th className="text-right">Late fees collected</th>
                  </tr>
                </thead>
                <tbody>
                  {basis.filter((b) => (b.badDebt ?? 0) > 0 || b.nonCollect > 0 || (b.lateFee ?? 0) > 0).map((b) => {
                    const d = b.nonCollect - (b.badDebt ?? 0);
                    const close = b.badDebt ? Math.abs(d) / b.badDebt <= 0.15 : false;
                    return (
                      <tr key={b.year} className="border-b border-dashed border-slate-100">
                        <td className="py-1.5 font-bold">{b.year}</td>
                        <td className="text-right tabular-nums">{b.badDebt === null ? "—" : fmtUSD(b.badDebt)}</td>
                        <td className="text-right tabular-nums font-semibold">{fmtUSD(b.nonCollect)}</td>
                        <td className={`text-right tabular-nums ${close ? "text-emerald-600 font-bold" : "text-slate-500"}`}>
                          {b.badDebt === null ? "—" : `${d >= 0 ? "+" : "−"}${fmtUSD(Math.abs(d))}`}
                        </td>
                        <td className="text-right tabular-nums text-emerald-700">{b.lateFee === null ? "—" : fmtUSD(b.lateFee)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-[10.5px] text-slate-400">
              Peach Duck books none of this in QuickBooks from May 2026 — the deal is the record there, which is
              why its credit-memo column stops. Late fees are real income that appears nowhere else on this board.
            </div>
          </div>
        )}

        <div className="mt-2 text-[10.5px] text-slate-400">
          Booked figures are hand-imported from QuickBooks P&amp;L by Month (Bardin Roofing LLC + Peach Duck Contracting,
          pool revenue excluded). There is no live QuickBooks feed — re-import after each month close.
          Years marked <b>Zoho</b> come from the pre-CRM export (initial invoice, dated by preferred build date);
          it tracks CRM build revenue within a few percent through 2025, which is why it&rsquo;s trusted for the
          years this CRM can&rsquo;t answer. Supplements weren&rsquo;t tracked separately in Zoho.
          The booked supplement split comes from the QuickBooks Sales by Customer Type Detail export; a ⚠ means
          some months of that year have no split imported yet, so the figure understates.
        </div>
      </div>

      {/* Breakdowns */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-3">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h2 className="text-[15px] font-extrabold">🔍 Breakdowns — {PERIOD_LABELS[period]}</h2>
          <div className="flex gap-1 flex-wrap">
            {(Object.keys(BREAKDOWN_LABELS) as BreakdownDim[]).map((k) => (
              <button key={k} onClick={() => setDim(k)}
                className={`px-2.5 py-1 rounded-full text-[11.5px] font-bold border ${dim === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>
                {BREAKDOWN_LABELS[k]}
              </button>
            ))}
          </div>
        </div>
        {dim === "carrier" && bdRows.some((r) => r.key === "__none") && (
          <div className="mb-2 text-[11.5px] bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-3 py-1.5">
            ⚠ {bdRows.find((r) => r.key === "__none")?.jobs ?? 0} jobs in this period have no carrier recorded ({fmtUSD(bdRows.find((r) => r.key === "__none")?.revenue ?? 0)}) — fix them on the deal page and this chart gets truer.
          </div>
        )}
        {dim === "rs" && bdRows.some((r) => r.key === "__none") && (
          <div className="mb-2 text-[11.5px] bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-3 py-1.5">
            ⚠ {bdRows.find((r) => r.key === "__none")?.jobs ?? 0} jobs in this period have no roof specialist assigned ({fmtUSD(bdRows.find((r) => r.key === "__none")?.revenue ?? 0)}) — set the RS on the deal page and this chart gets truer.
          </div>
        )}
        <div className="space-y-2">
          {bdRows.slice(0, 12).map((r) => {
            const max = bdRows[0]?.revenue || 1;
            return (
              <div key={r.key}>
                <div className="flex justify-between text-[12.5px] font-bold gap-2">
                  <span className="flex items-center gap-1.5">
                    {r.name}
                    {r.concentration && <Chip tone="amber" title="Over 25% of period revenue — concentration risk">{Math.round(r.share * 100)}% of revenue</Chip>}
                  </span>
                  <span className="tabular-nums">{fmtUSD(r.revenue)}</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden my-0.5">
                  <div className={`h-full rounded-full ${r.key === "__none" ? "bg-amber-400" : "bg-gradient-to-r from-orange-400 to-orange-600"}`} style={{ width: `${(r.revenue / max) * 100}%` }} />
                </div>
                <div className="flex justify-between text-[10.5px] text-slate-500">
                  <span>{r.jobs} jobs{r.gross !== null && r.revenue > 0 ? ` · ${((r.gross / r.revenue) * 100).toFixed(0)}% margin` : ""}</span>
                  <span>{r.avg !== null ? `${fmtUSD(r.avg)}/job` : ""}</span>
                </div>
              </div>
            );
          })}
          {bdRows.length === 0 && <div className="text-slate-400 text-[12.5px]">No completed builds in this period.</div>}
        </div>
      </div>

      {/* Roof specialists */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-3">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h2 className="text-[15px] font-extrabold">🪜 Roof specialists — {PERIOD_LABELS[period]}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={rsCarrier} onChange={(e) => setRsCarrier(e.target.value)}
              title="Filter every column to one carrier's claims"
              className={`border rounded-lg px-2 py-1 text-[12px] font-semibold ${rsCarrier ? "border-sky-400 bg-sky-50 text-sky-800" : "border-slate-300 text-slate-600"}`}>
              <option value="">All carriers</option>
              {/* "" is a real carrier key (not recorded) — sentinel so it can't collide with All */}
              {rsCarriers.map(([k, n]) => <option key={k || "__none"} value={k || "__none"}>{carrierDisplay(k)} ({n})</option>)}
            </select>
            <Chip tone="slate">outcomes on pre-inspected claims only · approval % = approved ÷ decided</Chip>
          </div>
        </div>
        {rsPerf.length === 0 ? (
          <div className="text-slate-400 text-[12.5px]">
            No pre-inspections or adjuster meetings {rsCarrier ? `for ${carrierDisplay(rsCarrier === "__none" ? "" : rsCarrier)} ` : ""}in this period.
          </div>
        ) : (
          <>
            {rsPreSummary.withPre.decided > 0 && rsPreSummary.withoutPre.decided > 0 && (
              <div className="mb-2 text-[11.5px] bg-sky-50 border border-sky-200 text-sky-800 rounded-lg px-3 py-1.5">
                🪜 Claims with a pre-inspection get approved{" "}
                <b>{rsPreSummary.withPre.pct?.toFixed(0)}%</b> of the time ({rsPreSummary.withPre.approved} of {rsPreSummary.withPre.decided} decided)
                {" "}vs <b>{rsPreSummary.withoutPre.pct?.toFixed(0)}%</b> without a recorded climb ({rsPreSummary.withoutPre.approved} of {rsPreSummary.withoutPre.decided}).
                {" "}Climbs that never got marked PRE Done count as &quot;without&quot; — marking them sharpens both numbers.
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-[12px] min-w-[840px]">
                <thead><tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-1.5">Roof specialist</th>
                  <th className="text-right" title="Completed pre-inspection climbs, by climb date">Pre-insp</th>
                  <th className="text-right" title="All adjuster meetings run in the period, climbed or not">Meetings</th>
                  <th className="text-right">2nd adj</th>
                  <th className="text-right" title="Outcome columns cover ONLY claims with a completed pre-inspection">Approved</th>
                  <th className="text-right">Denied</th>
                  <th className="text-right">Open</th>
                  <th className="text-right" title="Of the pre-inspected claims, how many went through the reinspection path before landing where they are">Reinspected</th>
                  <th className="text-right" title="Approved ÷ decided, on pre-inspected claims only">Approval</th>
                  <th className="text-right">Built jobs</th>
                  <th className="text-right">Built revenue</th>
                </tr></thead>
                <tbody>
                  {rsPerf.map((r) => (
                    <tr key={r.key} className={`border-b border-slate-100 ${r.key === "__none" ? "text-amber-700" : ""}`}>
                      <td className="py-1.5 font-semibold">
                        {r.name}
                        {r.preDecided > 0 && r.preDecided < 8 && <span className="text-amber-600 text-[10px] font-bold ml-1" title="Small sample — read the approval % directionally">n={r.preDecided}</span>}
                      </td>
                      <td className="text-right tabular-nums font-semibold">{r.preInspections || "—"}</td>
                      <td className="text-right tabular-nums">{r.meetings}</td>
                      <td className="text-right tabular-nums">{r.reinspMeetings || "—"}</td>
                      <td className="text-right tabular-nums text-emerald-700 font-semibold">{r.preApproved}</td>
                      <td className="text-right tabular-nums">{r.preDenied}</td>
                      <td className="text-right tabular-nums">
                        {r.preOpen}
                        {r.preLost > 0 && <span className="text-slate-400" title="Closed before the carrier decided (homeowner walked, cancelled) — never counts against approval"> +{r.preLost} lost</span>}
                      </td>
                      <td className="text-right tabular-nums" title={r.preReinspected > 0 ? `${r.preReinspected} of the pre-inspected claims took the reinspection path; ${r.preReinspWon} ended approved` : undefined}>
                        {r.preReinspected || "—"}
                        {r.preReinspWon > 0 && <span className="text-emerald-600 font-semibold"> ({r.preReinspWon} won)</span>}
                      </td>
                      <td className={`text-right font-bold ${r.preApprovalPct === null || r.preDecided < 8 ? "text-slate-400" : "text-sky-700"}`}
                        title={r.preDecided > 0 ? `${r.preApproved} approved of ${r.preDecided} decided pre-inspected claims` : undefined}>
                        {r.preApprovalPct === null ? "—" : `${r.preApprovalPct.toFixed(0)}%`}
                      </td>
                      <td className="text-right tabular-nums">{r.builtJobs || "—"}</td>
                      <td className="text-right tabular-nums">{r.builtRevenue > 0 ? fmtUSD(r.builtRevenue) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[10.5px] text-slate-400 mt-2">
              Outcome columns (Approved / Denied / Open / Reinspected / Approval) cover ONLY claims with a completed pre-inspection — an RS is graded on roofs they actually climbed; the with-vs-without line above keeps the comparison.
              Pre-insp = completed climbs by their completion date, credited to the recorded climber — falling back to the deal&apos;s RS when the climb wasn&apos;t explicitly assigned.
              Meetings count ALL 1st + 2nd adjuster meetings by MEETING date, climbed or not; outcomes show those claims&apos; current status, so recent meetings sit in Open until the carrier decides.
              Reinspected = claims that went through the reinspection fight (partial approval or a contested denial) before their current outcome — &quot;won&quot; = ended approved after that fight.
              Lost = closed before a decision — excluded from approval %. Built jobs &amp; revenue anchor on build-completion date, the same basis as the P&amp;L and Breakdowns above.
            </div>
          </>
        )}
      </div>

      {/* Supplement capture */}
      {supp && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[15px] font-extrabold">📑 Supplement capture — {PERIOD_LABELS[period]}</h2>
            <Chip tone="slate">requested vs approved by insurance</Chip>
          </div>
          {supp.jobsWithSupps === 0 ? (
            <div className="text-slate-400 text-[12.5px]">No jobs with supplements in this period.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                {[
                  ["Jobs with supps", String(supp.jobsWithSupps)],
                  ["Requested", fmtUSD(supp.requested)],
                  ["Approved", fmtUSD(supp.approved)],
                  ["Capture rate", supp.capturePct === null ? "—" : `${supp.capturePct.toFixed(0)}%`],
                ].map(([l, v]) => (
                  <div key={l} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{l}</div>
                    <div className="text-[17px] font-extrabold tabular-nums">{v}</div>
                  </div>
                ))}
              </div>
              <div className="text-[11.5px] text-slate-500 mb-2">Approved supplements = {supp.suppPctOfRevenue?.toFixed(1) ?? "—"}% of period revenue.</div>
              <div className="overflow-x-auto">
                <table className="w-full text-[12px] min-w-[520px]">
                  <thead><tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                    <th className="py-1.5">Carrier</th><th className="text-right">Jobs</th><th className="text-right">Requested</th><th className="text-right">Approved</th><th className="text-right">Capture</th>
                  </tr></thead>
                  <tbody>
                    {supp.byCarrier.map((c) => (
                      <tr key={c.name} className="border-b border-slate-100">
                        <td className="py-1.5 font-semibold">{c.name}{c.n < 5 && <span className="text-amber-600 text-[10px] font-bold ml-1">n={c.n}</span>}</td>
                        <td className="text-right tabular-nums">{c.n}</td>
                        <td className="text-right tabular-nums">{fmtUSD(c.requested)}</td>
                        <td className="text-right tabular-nums">{fmtUSD(c.approved)}</td>
                        <td className={`text-right font-bold ${c.pct !== null && c.pct >= 70 ? "text-emerald-600" : c.pct !== null && c.pct >= 40 ? "text-amber-600" : "text-rose-600"}`}>{c.pct === null ? "—" : `${c.pct.toFixed(0)}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Claims lens */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-3">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h2 className="text-[15px] font-extrabold">🎯 Claims lens — carrier × zip</h2>
          <Chip tone="slate">approval % = approved ÷ decided · n always shown</Chip>
        </div>
        <div className="flex gap-2 flex-wrap items-end mb-3">
          <label className="flex flex-col gap-0.5 text-[12px]">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Carrier</span>
            <select value={clCarrier} onChange={(e) => setClCarrier(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5 min-w-[160px]">
              <option value="">All carriers</option>
              {/* "" is a real carrier key (not recorded) — sentinel so it can't collide with All */}
              {clCarriers.map(([k, n]) => <option key={k || "__none"} value={k || "__none"}>{carrierDisplay(k)} ({n})</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-0.5 text-[12px]">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Zip</span>
            <select value={clZip} onChange={(e) => setClZip(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5 min-w-[150px]">
              <option value="">All zips</option>
              {clZips.map(([z, v]) => <option key={z} value={z}>{z} · {v.city} ({v.n})</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-0.5 text-[12px]">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Claims filed from</span>
            <input type="date" value={clFrom || claimsBounds.min} onChange={(e) => setClFrom(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5" />
          </label>
          <label className="flex flex-col gap-0.5 text-[12px]">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Through</span>
            <input type="date" value={clTo || claimsBounds.max} onChange={(e) => setClTo(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5" />
          </label>
          <button onClick={() => { setClCarrier(""); setClZip(""); setClFrom(""); setClTo(""); }}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-[12px] font-bold">Reset</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          {[
            ["Claims filed", String(clA.c), ""],
            ["Approved", String(clA.ap), "text-emerald-600"],
            ["Denied", String(clA.de), "text-rose-600"],
            ["Approval rate", clA.dec ? `${Math.round((clA.ap / clA.dec) * 100)}%` : "—", ""],
            ["Builds", String(clA.bu), ""],
          ].map(([l, v, cls]) => (
            <div key={l} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{l}</div>
              <div className={`text-[18px] font-extrabold tabular-nums ${cls}`}>{v}</div>
              {l === "Approval rate" && <div className="text-[10px] text-slate-500">of {clA.dec} decided{clA.dec > 0 && clA.dec < 8 ? " · small sample" : ""}</div>}
              {l === "Claims filed" && <div className="text-[10px] text-slate-500">{clA.op} open · {clA.lo} lost w/o decision</div>}
            </div>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px] min-w-[720px]">
            <thead><tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="py-1.5">{clDim === 0 ? "Carrier" : "Zip"}</th><th className="text-right">Claims</th><th className="text-right">Appr</th><th className="text-right">Denied</th><th className="text-right">Rate</th><th className="text-right">Lost</th><th className="text-right">Open</th><th className="text-right">Builds</th>
            </tr></thead>
            <tbody>
              {clGroups.map(([k, rows]) => {
                const a = claimsAgg(rows);
                const rate = a.dec ? Math.round((a.ap / a.dec) * 100) : null;
                return (
                  <tr key={k || "_"} className="border-b border-slate-100">
                    <td className="py-1.5 font-semibold">
                      {clDim === 0 ? carrierDisplay(k) : k}
                      {a.dec > 0 && a.dec < 8 && <span className="text-amber-600 text-[10px] font-bold ml-1" title="under 8 decided claims — read with care">n={a.dec}</span>}
                    </td>
                    <td className="text-right tabular-nums">{a.c}</td>
                    <td className="text-right tabular-nums">{a.ap}</td>
                    <td className="text-right tabular-nums">{a.de}</td>
                    <td className={`text-right font-bold ${rate === null || a.dec < 8 ? "text-slate-400" : rate >= 20 ? "text-emerald-600" : rate >= 10 ? "text-amber-600" : "text-rose-600"}`}>{rate === null ? "—" : `${rate}%`}</td>
                    <td className="text-right tabular-nums">{a.lo}</td>
                    <td className="text-right tabular-nums">{a.op}</td>
                    <td className="text-right tabular-nums">{a.bu}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-[10.5px] text-slate-500 mt-2">
          Deals lost before the carrier answered never count against a carrier. "Denied" includes claims still being fought through reinspection — a reinspection win flips them to approved automatically. Claim-date tracking began with the CRM; older builds predate it.
        </div>
      </div>

      {/* Material invoices */}
      <div className="mb-3">
        <MaterialInvoicesPanel invoices={raw.invoices} onChanged={() => { void load(); }} />
      </div>

      {/* Job table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 mb-3">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h2 className="text-[15px] font-extrabold">
            🏠 Job-by-job P&amp;L — {PERIOD_LABELS[period]}
            <Link to="/jobs" className="ml-2 text-[12px] font-bold text-orange-600 hover:underline">Per-square job analysis</Link>
          </h2>
          <div className="flex gap-1">
            {([["margin", "Lowest margin"], ["revenue", "Biggest revenue"], ["date", "Most recent"]] as Array<["margin" | "revenue" | "date", string]>).map(([k, l]) => (
              <button key={k} onClick={() => setJobSort(k)}
                className={`px-2.5 py-1 rounded-full text-[11.5px] font-bold border ${jobSort === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px] min-w-[940px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b-2 border-slate-200">
                <th className="py-2">Job</th><th>Built</th><th className="text-right">Sq</th><th className="text-right">Revenue</th>
                <th className="text-right">Materials</th><th className="text-right">Labor</th><th className="text-right">Comm.</th>
                <th className="text-right">Margin</th><th className="text-right">%</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs.slice(0, 40).map((j) => {
                const d = j.deal;
                const open = expandedJob === d.id;
                return (
                  <Fragment key={d.id}>
                    <tr className="border-b border-slate-100 hover:bg-orange-50/50 cursor-pointer" onClick={() => setExpandedJob(open ? null : d.id)}>
                      <td className="py-2 pr-2">
                        <div className="font-bold" onClick={(e) => e.stopPropagation()}><DealLink id={d.id}>{d.homeowner_name ?? "—"}</DealLink></div>
                        <div className="text-[11px] text-slate-500">{d.property_street}{d.property_city ? ` · ${d.property_city}` : ""}</div>
                      </td>
                      <td className="whitespace-nowrap">{d.build_completed_date ?? "—"}</td>
                      <td className="text-right tabular-nums">{j.squares !== null ? j.squares.toFixed(1) : "—"}</td>
                      <td className="text-right tabular-nums font-bold">{j.invoicePending ? <span className="text-slate-400">—</span> : fmtUSD(j.revenue)}</td>
                      <td className="text-right tabular-nums">{j.materials === null ? <span className="text-slate-400">—</span> : fmtUSD(j.materials)}</td>
                      <td className="text-right tabular-nums">{j.labor === null ? <span className="text-slate-400">—</span> : fmtUSD(j.labor)}</td>
                      <td className="text-right tabular-nums">{j.commissions > 0 ? fmtUSD(j.commissions) : <span className="text-slate-400">—</span>}</td>
                      <td className={`text-right tabular-nums font-bold ${pctCls(j.marginPct)}`}>{j.gross === null ? "—" : fmtUSD(j.gross)}</td>
                      <td className={`text-right font-bold ${pctCls(j.marginPct)}`}>{j.marginPct === null ? "—" : `${j.marginPct.toFixed(1)}%`}</td>
                      <td className="whitespace-nowrap">
                        {j.invoicePending && <Chip tone="amber">invoice pending</Chip>}
                        {!j.invoicePending && j.materialsUnpaid > 0 && <Chip tone="rose" title="SRS invoice unpaid">SRS owed</Chip>}
                        {!j.invoicePending && j.laborUnpaid > 0 && <Chip tone="rose">crew owed</Chip>}
                        {!j.invoicePending && !j.commissionPaid && j.commissions > 0 && <Chip tone="amber">comm. unpaid</Chip>}
                        {!j.invoicePending && j.materialsUnpaid === 0 && j.laborUnpaid === 0 && (j.commissionPaid || j.commissions === 0) && <Chip tone="emerald">all paid</Chip>}
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-b border-slate-100">
                        <td colSpan={10} className="bg-slate-50 px-4 py-3">
                          <div className="grid md:grid-cols-3 gap-3 text-[12px]">
                            <div className="bg-white border border-slate-200 rounded-lg p-2.5">
                              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">🧾 Material invoices</div>
                              {(invByDeal.get(d.id) ?? []).map((inv) => (
                                <div key={inv.id} className="flex justify-between py-0.5">
                                  <span>{inv.invoice_number} · {inv.invoice_date}</span>
                                  <span className="tabular-nums font-bold">{fmtUSD(num(inv.total))} <span className={inv.paid_status === "paid" ? "text-emerald-600" : "text-rose-600"}>· {inv.paid_status}{inv.paid_status === "paid" && inv.paid_at ? ` ${inv.paid_at.slice(5, 7)}/${inv.paid_at.slice(8, 10)}` : ""}</span></span>
                                </div>
                              ))}
                              {(invByDeal.get(d.id) ?? []).length === 0 && <div className="text-slate-400">none matched yet</div>}
                            </div>
                            <div className="bg-white border border-slate-200 rounded-lg p-2.5">
                              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">🔨 Crew payments</div>
                              {(crewByDeal.get(d.id) ?? []).map((c) => (
                                <div key={c.id} className="flex justify-between py-0.5">
                                  <span>{num(c.squares) > 0 ? `${num(c.squares)} sq` : "flat"} · {c.status}</span>
                                  <span className="tabular-nums font-bold">{fmtUSD(num(c.total))}</span>
                                </div>
                              ))}
                              {(crewByDeal.get(d.id) ?? []).length === 0 && <div className="text-slate-400">no crew payment recorded</div>}
                            </div>
                            <div className="bg-white border border-slate-200 rounded-lg p-2.5">
                              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">💰 Commissions (accrued)</div>
                              <div className="flex justify-between py-0.5"><span>RM/TL/ASM + RS pool</span><span className="tabular-nums font-bold">{fmtUSD(j.commissions)}</span></div>
                              {(commSplit.get(d.id)?.review ?? 0) > 0 && (
                                <div className="flex justify-between py-0.5 text-slate-500"><span>incl. 5-Star Review SPIFF</span><span className="tabular-nums">{fmtUSD(commSplit.get(d.id)?.review ?? 0)}</span></div>
                              )}
                              <div className="text-slate-500">{d.commission_paid ? "paid" : d.commission_approved ? "approved · awaiting payout" : "in the pending queue"}</div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {sortedJobs.length === 0 && (
                <tr><td colSpan={10} className="py-6 text-center text-slate-400">No completed builds in this period.</td></tr>
              )}
            </tbody>
          </table>
          {sortedJobs.length > 40 && <div className="text-[11px] text-slate-400 pt-1">Showing 40 of {sortedJobs.length} — narrow the period to see the rest.</div>}
        </div>
        <div className="text-[10.5px] text-slate-500 mt-2">"—" means the data isn't recorded yet, never $0. Commission = full accrued load (RM + TL + ASM + RS pool) at each deal's real rates — same math as the Commission Board — plus the 5-Star Review SPIFF once it is booked (paid with the weekly SPIFFs).</div>
      </div>

      <div className="text-center text-[11px] text-slate-400 pb-4">
        Coverage honesty: crew-labor tracking began May 2026 · material invoices imported from RoofHub back to Apr 2024 · pre-2026 builds are legacy data.<br />
        Visible to Greg · Chad · Shannon · Jennifer only.
      </div>
    </div>
  );
}
